import React, { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import LanguageIcon from "@mui/icons-material/Language";
import CheckIcon from "@mui/icons-material/Check";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useTranslation } from "react-i18next";
import { type Language } from "../state/preferencesStore";

interface LanguageSelectorButtonProps {
  size?: "small" | "medium" | "large";
}

// Sits in the unauthenticated top bar, where there is no user whose stored preference
// we could read, so this drives i18next directly (its detector caches the choice). The
// signed-in equivalent is the picker in /profile, which writes to the server.
export const LanguageSelectorButton: React.FC<LanguageSelectorButtonProps> = ({
  size = "medium",
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const translations = {
    tooltip: t("language.selector_tooltip"),
    english: t("language.english"),
    spanish: t("language.spanish"),
    ariaLabel: t("language.selector_aria"),
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang: Language) => {
    i18n.changeLanguage(lang);
    handleClose();
  };

  return (
    <>
      <Tooltip title={translations.tooltip}>
        <IconButton
          color="inherit"
          onClick={handleClick}
          aria-label={translations.ariaLabel}
          aria-controls={open ? "language-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          size={size}
        >
          <LanguageIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "language-button",
        }}
      >
        <MenuItem onClick={() => handleLanguageChange("en")}>
          <ListItemIcon>
            {language === "en" ? <CheckIcon fontSize="small" /> : <span style={{ width: 20 }} />}
          </ListItemIcon>
          <ListItemText>{translations.english}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleLanguageChange("es-MX")}>
          <ListItemIcon>
            {language === "es-MX" ? <CheckIcon fontSize="small" /> : <span style={{ width: 20 }} />}
          </ListItemIcon>
          <ListItemText>{translations.spanish}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
